import { Flag, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useMarkWrong } from "./mark-wrong-api";

/**
 * A quiet "Mark as wrong" under an answer (Phase 5, 2026-09-26; the default of
 * the design's Q6, `document-ai/0030` §2.7.6).
 *
 * Any member of the project may mark an answer, and take the mark back. The
 * mark changes nothing on the answer. What it changes is what the Brain trusts
 * next time: what this answer read in images is not taken as enough again, so
 * the next question on those images looks at them afresh, and support sees
 * the looks in the backoffice.
 *
 * Quiet on purpose: most answers are right, and a loud control reads as an
 * invitation. The settled state says what the mark does, because "wrong"
 * alone invites the reader to expect the answer to change, and it will not.
 */
export function MarkWrong({
  projectId,
  threadId,
  messageId,
  markedWrong,
}: {
  projectId: string;
  threadId: string;
  messageId: string;
  /** As the stored answer says. */
  markedWrong: boolean;
}) {
  const mark = useMarkWrong(projectId, threadId);
  // While the request is out, the state asked for: a click that seems to do
  // nothing for a second gets clicked again.
  const shown = mark.isPending ? mark.variables.wrong : markedWrong;

  return (
    <div className="flex flex-col gap-1">
      {shown ? (
        <div className="flex flex-wrap items-center gap-2 text-[0.8rem] text-muted" role="status">
          <Flag size={14} aria-hidden className="text-danger" />
          <span>
            Marked as wrong. The Brain will look again at the images this answer relied on.
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={mark.isPending}
            onClick={() => mark.mutate({ messageId, wrong: false })}
          >
            <Undo2 size={14} aria-hidden />
            Undo
          </Button>
        </div>
      ) : (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="self-start text-muted"
          disabled={mark.isPending}
          onClick={() => mark.mutate({ messageId, wrong: true })}
        >
          <Flag size={14} aria-hidden />
          Mark as wrong
        </Button>
      )}
      {mark.isError && (
        <p role="alert" className="text-[0.8rem] font-semibold text-danger">
          {mark.error instanceof Error ? mark.error.message : "The mark could not be saved."}
        </p>
      )}
    </div>
  );
}
